import { captureEvent } from "@/app/_analytics/analytics";
import type { PendingExposure } from "@/app/_analytics/posthog-types";
import { usePostHogState } from "@/app/_providers/posthog";
import { CookieName } from "@/app/_utilities/cookieKeys";
import { EventName } from "@mw/shared/analyticsEventNames";
import Cookies from "js-cookie";
import { usePathname } from "next/navigation";
import { useEffect } from "react";

const CONFIRMED_EXPOSURES_STORAGE_KEY = "mw_confirmed_ab_exposures";
const PENDING_EXPOSURES_COOKIE_MAX_AGE_DAYS = 5 / (24 * 60);

type JsonObject = Record<string, unknown>;
type ConfirmedExposures = Record<string, PendingExposure[]>;

function isPendingExposure(value: unknown): value is PendingExposure {
  if (typeof value !== "object" || value === null) return false;

  const exposure = value as Partial<Record<keyof PendingExposure, unknown>>;
  const hasValidVariant = typeof exposure.variant === "string" || typeof exposure.variant === "boolean";

  return typeof exposure.flagName === "string" && hasValidVariant && typeof exposure.pathname === "string";
}

function isRecord(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parsePendingExposures(value: string) {
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter(isPendingExposure);
  } catch {
    return [];
  }
}

function getConfirmedExposures() {
  try {
    const value = sessionStorage.getItem(CONFIRMED_EXPOSURES_STORAGE_KEY);
    if (!value) return {};

    const parsed: unknown = JSON.parse(value);
    if (!isRecord(parsed)) return {};

    return Object.entries(parsed).reduce<ConfirmedExposures>((confirmedExposures, [pathname, exposures]) => {
      if (!Array.isArray(exposures)) return confirmedExposures;

      const validExposures = exposures.filter(isPendingExposure);
      if (validExposures.length) {
        confirmedExposures[pathname] = validExposures;
      }

      return confirmedExposures;
    }, {});
  } catch {
    return {};
  }
}

function setConfirmedExposures(pathname: string, exposures: PendingExposure[]) {
  try {
    const confirmedExposures = getConfirmedExposures();
    confirmedExposures[pathname] = exposures;
    sessionStorage.setItem(CONFIRMED_EXPOSURES_STORAGE_KEY, JSON.stringify(confirmedExposures));
  } catch {
    // sessionStorage can be unavailable in restricted browser contexts.
  }
}

function setPendingExposures(exposures: PendingExposure[]) {
  if (!exposures.length) {
    Cookies.remove(CookieName.PendingExposures, { path: "/" });
    return;
  }

  Cookies.set(CookieName.PendingExposures, JSON.stringify(exposures), {
    expires: PENDING_EXPOSURES_COOKIE_MAX_AGE_DAYS,
    path: "/",
    sameSite: "strict",
  });
}

function captureExposures(exposures: PendingExposure[]) {
  exposures.forEach(exposure => {
    captureEvent(EventName.$feature_flag_called, {
      $feature_flag: exposure.flagName,
      $feature_flag_response: exposure.variant,
    });
  });
}

export const useCaptureExposures = () => {
  const pathname = usePathname();
  const isPostHogLoaded = usePostHogState(state => state.isLoaded);

  useEffect(() => {
    if (!pathname || !isPostHogLoaded) return;

    const pendingExposuresCookie = Cookies.get(CookieName.PendingExposures);
    const pendingExposures = pendingExposuresCookie ? parsePendingExposures(pendingExposuresCookie) : [];
    const matchingPendingExposures = pendingExposures.filter(exposure => exposure.pathname === pathname);
    const unmatchedPendingExposures = pendingExposures.filter(exposure => exposure.pathname !== pathname);

    if (matchingPendingExposures.length) {
      // The cookie can be created by a prefetch. Only after the actual pathname
      // matches do we promote it to confirmed, tab-scoped state.
      setConfirmedExposures(pathname, matchingPendingExposures);
      captureExposures(matchingPendingExposures);
      setPendingExposures(unmatchedPendingExposures);
      return;
    }

    // Keep unmatched pending exposures because they may have been seeded by
    // prefetches whose later real navigations are served from the Router Cache.

    // Later NextJS Router Cache navigations may skip middleware, so reuse only
    // assignments that were already confirmed by a real pathname match.
    captureExposures(getConfirmedExposures()[pathname] ?? []);
  }, [isPostHogLoaded, pathname]);
};
